import { useEffect, useState, useCallback } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowsClockwise, CheckCircle, CloudArrowDown, DownloadSimple, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

const ALL = "__ALL__";

// Helpers ──────────────────────────────────────────────────────────
const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const fmtDt = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
};

export default function AdminYkClicks() {
  // Filters
  const [accountId, setAccountId] = useState("");
  const [publisherId, setPublisherId] = useState("");
  const [advertiserId, setAdvertiserId] = useState("");
  const [startDate, setStartDate] = useState(daysAgo(7));
  const [endDate, setEndDate] = useState(today());
  const [search, setSearch] = useState("");

  // Filter dropdown options
  const [opts, setOpts] = useState({ accounts: [], publishers: [], advertisers: [] });

  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 50;

  // Data
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState({ total: 0, converted: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Fetch-from-YK (upstream report pull — max 14 days)
  const [syncAccountId, setSyncAccountId] = useState("");
  const [syncStart, setSyncStart] = useState(daysAgo(7));
  const [syncEnd, setSyncEnd] = useState(today());
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => {
    API.get("/admin/yk/filters")
      .then((r) => {
        const data = r?.data || { accounts: [], publishers: [], advertisers: [] };
        setOpts(data);
        // Prefill sync account when there's exactly one lease.
        if (!syncAccountId && (data.accounts || []).length === 1) {
          setSyncAccountId(data.accounts[0].id);
        }
      })
      .catch(() => { /* leave empty — filters degrade to free-text search */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (accountId) params.account_id = accountId;
      if (publisherId) params.publisher_id = publisherId;
      if (advertiserId) params.advertiser_id = advertiserId;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (search.trim()) params.search = search.trim();
      const { data } = await API.get("/admin/yk/clicks", { params });
      setRows(data?.data || []);
      setTotal(data?.total || 0);
      setTotals(data?.totals || { total: data?.total || 0, converted: 0 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load clicks");
    } finally {
      setLoading(false);
    }
  }, [accountId, publisherId, advertiserId, startDate, endDate, search, page]);

  useEffect(() => {
    // Debounce the search input so per-keystroke doesn't hammer Mongo.
    const t = setTimeout(fetchRows, 250);
    return () => clearTimeout(t);
  }, [fetchRows]);

  const handleFetchFromYk = async () => {
    if (!syncStart || !syncEnd) {
      toast.error("Pick both start and end dates");
      return;
    }
    if (syncStart > syncEnd) {
      toast.error("Start date must be on or before end date");
      return;
    }
    // YK retains ~14 days of click history — surface the limit before the call.
    const startMs = new Date(`${syncStart}T00:00:00Z`).getTime();
    const endMs = new Date(`${syncEnd}T23:59:59Z`).getTime();
    const ageDays = (Date.now() - startMs) / (86400 * 1000);
    const spanDays = (endMs - startMs) / (86400 * 1000);
    if (ageDays > 14.5) {
      toast.error("Yieldkit only retains ~14 days of click history — pick a more recent start date");
      return;
    }
    if (spanDays > 14.5) {
      toast.error("Date window must be 14 days or less");
      return;
    }

    setSyncing(true);
    setSyncResult(null);
    try {
      const body = { start_date: syncStart, end_date: syncEnd };
      const url = syncAccountId
        ? `/admin/yk/accounts/${syncAccountId}/sync/clicks`
        : "/admin/yk/sync/clicks";
      const { data } = await API.post(url, body);
      setSyncResult(data);
      const results = data?.results || [];
      const rowsSeen = results.reduce((n, r) => n + (r.clicks?.rows || 0), 0);
      const upserted = results.reduce((n, r) => n + (r.clicks?.upserted || 0), 0);
      const failed = results.filter((r) => r.ok === false);
      if (failed.length) {
        toast.error(failed[0].error || "Clicks sync failed for one or more accounts");
      } else {
        toast.success(
          `Fetched ${rowsSeen} click row(s) · ${upserted} upserted across ${results.length || 0} account(s)`,
        );
        // Align the browse filter with the window we just pulled, then reload.
        setStartDate(syncStart);
        setEndDate(syncEnd);
        if (syncAccountId) setAccountId(syncAccountId);
        setPage(1);
        setTimeout(fetchRows, 100);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to fetch clicks from Yieldkit");
    } finally {
      setSyncing(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const downloadCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (accountId) params.set("account_id", accountId);
      if (publisherId) params.set("publisher_id", publisherId);
      if (advertiserId) params.set("advertiser_id", advertiserId);
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      if (search.trim()) params.set("search", search.trim());
      const baseURL = API.defaults?.baseURL || "";
      const token = localStorage.getItem("token") || "";
      const resp = await fetch(`${baseURL}/admin/yk/clicks/export.csv?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `yieldkit-clicks-${startDate}-to-${endDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="admin-yk-clicks-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 font-bold tracking-[0.2em] uppercase">YIELDKIT</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Click Logs</h1>
          <p className="text-sm text-slate-500 mt-1">
            Outbound YK redirects we issued, plus optional pulls from Yieldkit&apos;s click report.
            Independent feed — does not join with the conversions table.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={downloadCsv}
            disabled={exporting || total === 0}
            data-testid="yk-clicks-export"
          >
            <DownloadSimple size={14} className={`mr-1.5 ${exporting ? "animate-pulse" : ""}`} />
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchRows} data-testid="yk-clicks-refresh">
            <ArrowsClockwise size={14} className="mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Fetch from Yieldkit API */}
      <div
        className="border border-slate-200 rounded-md p-5 bg-white"
        data-testid="yk-clicks-sync-card"
      >
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#002FA7]">
            Yieldkit Reporting API · /reports/clicks
          </p>
          <span className="text-[10px] font-mono text-slate-400">retention ≈ 14 days</span>
        </div>
        <h3 className="font-heading text-base font-semibold text-slate-900">
          Fetch clicks from Yieldkit
        </h3>
        <p className="text-xs text-slate-500 mt-1 mb-3">
          Pulls <code className="font-mono bg-slate-100 px-1 rounded text-[11px]">/api/v3/reports/clicks</code> for
          the chosen lease + window (requires <code className="font-mono bg-slate-100 px-1 rounded text-[11px]">site_id</code>).
          Upserts into <code className="font-mono bg-slate-100 px-1 rounded text-[11px]">yk_clicks</code> by RID —
          live redirect rows keep their richer local fields.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[200px]">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">YK Account</Label>
            <Select
              value={syncAccountId || ALL}
              onValueChange={(v) => setSyncAccountId(v === ALL ? "" : v)}
              disabled={syncing}
            >
              <SelectTrigger data-testid="yk-clicks-sync-account">
                <SelectValue placeholder="All assigned accounts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All assigned accounts</SelectItem>
                {opts.accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.label || a.id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Start date</Label>
            <Input
              type="date"
              value={syncStart}
              onChange={(e) => setSyncStart(e.target.value)}
              disabled={syncing}
              data-testid="yk-clicks-sync-start"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">End date</Label>
            <Input
              type="date"
              value={syncEnd}
              onChange={(e) => setSyncEnd(e.target.value)}
              disabled={syncing}
              data-testid="yk-clicks-sync-end"
            />
          </div>
          <Button
            onClick={handleFetchFromYk}
            disabled={syncing}
            className="bg-[#002FA7] hover:bg-[#002FA7]/90 text-white"
            data-testid="yk-clicks-sync-trigger"
          >
            <CloudArrowDown size={14} className="mr-2" />
            {syncing ? "Fetching…" : "Fetch from Yieldkit"}
          </Button>
          {syncResult && (
            <div className="text-xs text-slate-600 font-mono" data-testid="yk-clicks-sync-result">
              {(syncResult.results || []).map((r, i) => (
                <div key={i}>
                  {r.label || r.account_id}:{" "}
                  {r.ok
                    ? `${r.clicks?.rows ?? 0} rows · ${r.clicks?.upserted ?? 0} upserted`
                    : `failed — ${r.error}`}
                </div>
              ))}
            </div>
          )}
        </div>
        {syncResult?.results?.some((r) => r.ok && (r.clicks?.rows ?? 0) === 0 && r.debug) ? (
          <pre
            className="mt-3 text-[10px] bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto whitespace-pre-wrap"
            data-testid="yk-clicks-sync-debug"
          >
            {JSON.stringify(
              (syncResult.results || []).map((r) => ({ label: r.label, debug: r.debug })),
              null,
              2,
            )}
          </pre>
        ) : null}
      </div>

      {/* Filter bar */}
      <div
        className="rounded-md border border-slate-200 bg-slate-50 p-4 grid grid-cols-1 md:grid-cols-6 gap-3"
        data-testid="yk-clicks-filters"
      >
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Start date</Label>
          <Input
            type="date" value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
            data-testid="yk-clicks-start"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">End date</Label>
          <Input
            type="date" value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
            data-testid="yk-clicks-end"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Publisher</Label>
          <Select
            value={publisherId || ALL}
            onValueChange={(v) => { setPublisherId(v === ALL ? "" : v); setPage(1); }}
          >
            <SelectTrigger data-testid="yk-clicks-publisher">
              <SelectValue placeholder="All publishers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All publishers</SelectItem>
              {opts.publishers.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Offer</Label>
          <Select
            value={advertiserId || ALL}
            onValueChange={(v) => { setAdvertiserId(v === ALL ? "" : v); setPage(1); }}
          >
            <SelectTrigger data-testid="yk-clicks-offer">
              <SelectValue placeholder="All offers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All offers</SelectItem>
              {opts.advertisers.map((a) => (
                <SelectItem key={a.advertiser_id} value={a.advertiser_id}>
                  {a.name || a.advertiser_id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">YK Account</Label>
          <Select
            value={accountId || ALL}
            onValueChange={(v) => { setAccountId(v === ALL ? "" : v); setPage(1); }}
          >
            <SelectTrigger data-testid="yk-clicks-account">
              <SelectValue placeholder="All accounts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All accounts</SelectItem>
              {opts.accounts.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.label || a.id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Search</Label>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="RID / sub_id / advertiser"
              className="pl-8"
              data-testid="yk-clicks-search"
            />
          </div>
        </div>
      </div>

      {/* Period totals for the full filtered window */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm" data-testid="yk-clicks-rollup">
        <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Clicks (period)</div>
          <div className="font-heading text-xl font-bold text-slate-900 mt-1">{(totals.total ?? total).toLocaleString()}</div>
          <div className="text-[10px] text-slate-400 mt-0.5">{startDate} → {endDate}</div>
        </div>
      </div>

      {/* Table */}
      <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Date</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Publisher</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Offer</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Click ID (RID)</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Sub IDs</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Geo / IP</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">User Agent</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Account</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400 text-sm">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-500 text-sm">No clicks for the selected filters.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.rid} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`yk-click-row-${r.rid}`}>
                <td className="px-3 py-2 text-[11px] text-slate-700 whitespace-nowrap">{fmtDt(r.click_date)}</td>
                <td className="px-3 py-2 text-[11px] text-slate-700">
                  {r.publisher_email ? (
                    <>
                      <div>{r.publisher_email}</div>
                      {r.publisher_name ? <div className="text-[10px] text-slate-400">{r.publisher_name}</div> : null}
                    </>
                  ) : (
                    <span className="font-mono text-[10px] text-slate-400">{r.publisher_id || "—"}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-[11px]">
                  <div className="font-semibold text-slate-800">{r.advertiser_name || "—"}</div>
                </td>
                <td className="px-3 py-2 text-[11px] font-mono text-slate-600">{r.rid}</td>
                <td className="px-3 py-2 text-[11px] font-mono text-slate-600">
                  {[r.sub_id1, r.sub_id2, r.sub_id3].filter(Boolean).join(" · ") || "—"}
                </td>
                <td className="px-3 py-2 text-[11px] text-slate-600">
                  <div>{r.country || "—"}</div>
                  <div className="font-mono text-[10px] text-slate-400">{r.ip || ""}</div>
                </td>
                <td className="px-3 py-2 text-[10px] text-slate-500 max-w-[260px] truncate" title={r.ua}>{r.ua || "—"}</td>
                <td className="px-3 py-2 text-[11px] text-slate-600">{r.account_label || "—"}</td>
                <td className="px-3 py-2 text-[11px]">
                  {r.converted ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700 text-[10px] font-bold uppercase tracking-wider">
                      <CheckCircle size={12} weight="fill" /> Converted
                    </span>
                  ) : r.redirect_ok ? (
                    <span className="text-slate-500 text-[10px]">
                      {r.source === "yk_api" ? "From YK API" : "Redirected"}
                    </span>
                  ) : (
                    <span className="text-rose-700 text-[10px] font-semibold">Failed</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-[11px] text-slate-500">
        <div>
          {total.toLocaleString()} click{total === 1 ? "" : "s"} · page <span className="font-mono font-bold">{page}</span> / <span className="font-mono">{totalPages}</span>
        </div>
        <div className="flex gap-1.5">
          <Button
            variant="outline" size="sm"
            disabled={page <= 1 || loading}
            onClick={() => setPage(page - 1)}
            data-testid="yk-clicks-prev"
          >Prev</Button>
          <Button
            variant="outline" size="sm"
            disabled={loading || page >= totalPages}
            onClick={() => setPage(page + 1)}
            data-testid="yk-clicks-next"
          >Next</Button>
        </div>
      </div>
    </div>
  );
}
